import { useEffect, useState } from "react";
import { EyeOff, Copy, Plus, RotateCw, Trash2, Check, Zap, AlertCircle } from "lucide-react";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { Switch } from "@/ui/components/primitives/switch";
import { Badge } from "@/ui/components/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/components/primitives/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/ui/components/primitives/dropdown-menu";
import { Alert, AlertDescription, AlertTitle } from "@/ui/components/primitives/alert";
import { listApiKeys, createApiKey, deleteApiKey } from "@/services/api/services/api-keys";
import { listWebhooks, createWebhook, validateWebhookUrl, deleteWebhook } from "@/services/api/services/webhooks";
import type { ApiKey, Webhook } from "@/modules/shared/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/ui/components/primitives/dialog";
import { StatusBadge } from "@/modules/shared/components/status-badge";
import { PermissionGate } from "@/access-control/core/permission";

export function ApiKeysPage() {
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [keyEnv, setKeyEnv] = useState<"live" | "test">("live");
  const [keyPerms, setKeyPerms] = useState<Record<string, boolean>>({ payments: true, wallets: true, webhooks: true, payouts: true });
  const [keySecret, setKeySecret] = useState<string | null>(null);
  const [keyPrefix, setKeyPrefix] = useState<string | null>(null);
  const [creatingKey, setCreatingKey] = useState(false);
  const [whDialogOpen, setWhDialogOpen] = useState(false);
  const [whUrl, setWhUrl] = useState("");
  const [whEvents, setWhEvents] = useState<Record<string, boolean>>({ "payment.captured": true, "payment.failed": true, "payout.completed": true, "payout.failed": true });
  const [whDesc, setWhDesc] = useState("");
  const [whActive, setWhActive] = useState(true);
  const [whValidation, setWhValidation] = useState<{ ok: boolean; msg: string } | null>(null);
  const [whSecret, setWhSecret] = useState<string | null>(null);
  const [creatingWebhook, setCreatingWebhook] = useState(false);

  const toggleKeyVisibility = (id: string) => {
    setVisibleKeys((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyKey = (key: string, id: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  async function loadKeys() {
    setLoadingKeys(true);
    try { const data = await listApiKeys(); setKeys(data); } finally { setLoadingKeys(false); }
  }
  async function loadWebhooks() {
    setLoadingWebhooks(true);
    try { const data = await listWebhooks(1, 10); setWebhooks(data.webhooks); } finally { setLoadingWebhooks(false); }
  }

  useEffect(() => { loadKeys(); loadWebhooks(); }, []);

  const handleDeleteWebhook = async (id: string) => {
    if (confirm("Are you sure you want to delete this webhook? This action cannot be undone.")) {
      try {
        await deleteWebhook(id);
        await loadWebhooks();
      } catch (error) {
        console.error("Failed to delete webhook:", error);
      }
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    if (confirm("Are you sure you want to delete this API key? This action cannot be undone and will immediately invalidate the key for all applications.")) {
      try {
        await deleteApiKey(id);
        await loadKeys();
      } catch (error) {
        console.error("Failed to delete API key:", error);
      }
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1>API Keys & Webhooks</h1>
          <p className="text-muted-foreground mt-1">
            Manage your API credentials and webhook endpoints
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Test</span>
            <Switch checked={isLiveMode} onCheckedChange={setIsLiveMode} />
            <span className="text-sm font-medium">Live</span>
          </div>
        </div>
      </div>

      {/* Mode Alert */}
      <Alert className={isLiveMode ? "border-primary bg-primary/5" : "border-warning bg-warning/5"}>
        <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
        <AlertTitle>
          {isLiveMode ? "Live Mode Active" : "Test Mode Active"}
        </AlertTitle>
        <AlertDescription>
          {isLiveMode
            ? "You are viewing live API keys. All transactions will be real."
            : "You are in test mode. Use test keys for development and testing."}
        </AlertDescription>
      </Alert>

      {/* API Keys Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3>API Keys</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Use these keys to authenticate your API requests
            </p>
          </div>
          <Button onClick={() => setKeyDialogOpen(true)} disabled={creatingKey}>
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Generate New Key
          </Button>
        </div>

        <div className="space-y-4">
          {(loadingKeys ? [] : keys)
            .filter((key) => isLiveMode ? key.environment === "PRODUCTION" : key.environment === "SANDBOX")
            .map((apiKey) => (
              <Card key={apiKey.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h4>{apiKey.keyName}</h4>
                      <Badge variant={apiKey.environment === "PRODUCTION" ? "default" : "secondary"}>
                        {apiKey.environment === "PRODUCTION" ? (
                          <Zap className="h-3 w-3 mr-1" strokeWidth={1.5} />
                        ) : null}
                        {apiKey.environment === "PRODUCTION" ? "LIVE" : "TEST"}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">Created on {new Date(apiKey.createdAt).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">Last used: {apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleString() : "Never"}</p>
                  </div>
                  <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteApiKey(apiKey.id)}
                      className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      title="Delete Key"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">API Key Prefix</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-10 px-3 rounded-lg border bg-muted/50 flex items-center">
                        <code className="flex-1 text-sm font-mono break-all">{apiKey.keyPrefix}</code>
                      </div>
                      <Button variant="outline" size="icon" onClick={() => navigator.clipboard.writeText(apiKey.keyPrefix)}>
                        <Copy className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
        </div>
      </Card>

      <Dialog open={keyDialogOpen} onOpenChange={setKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>Generate a new API key. The full key will be shown once; save it securely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!keySecret ? (
              <>
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Environment</Label>
                  <div className="flex items-center gap-3">
                    <Button variant={keyEnv === "live" ? "default" : "outline"} size="sm" onClick={() => setKeyEnv("live")}>Live</Button>
                    <Button variant={keyEnv === "test" ? "default" : "outline"} size="sm" onClick={() => setKeyEnv("test")}>Test</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Permissions</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(keyPerms).map((perm) => (
                      <label key={perm} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={keyPerms[perm]} onChange={(e) => setKeyPerms({ ...keyPerms, [perm]: e.target.checked })} />
                        {perm}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    onClick={async () => {
                      setCreatingKey(true);
                      try {
                        const payload = { name: keyName, environment: keyEnv, permissions: Object.keys(keyPerms).filter((k) => keyPerms[k]) };
                        const created = await createApiKey(payload);
                        setKeySecret(created.key || null);
                        setKeyPrefix(created.keyPrefix || null);
                        await loadKeys();
                      } finally {
                        setCreatingKey(false);
                      }
                    }}
                    loading={creatingKey}
                    loadingText="Creating..."
                  >
                    Create Key
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Alert>
                  <AlertTitle>Save this key now</AlertTitle>
                  <AlertDescription>The full key will not be shown again. Store it securely.</AlertDescription>
                </Alert>
                <div>
                  <Label>Key Prefix</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-10 px-3 rounded-lg border bg-muted/50 flex items-center">
                      <code className="flex-1 text-sm font-mono break-all">{keyPrefix}</code>
                    </div>
                    <Button variant="outline" size="icon" onClick={() => keyPrefix && navigator.clipboard.writeText(keyPrefix)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Secret (Full Key)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-10 px-3 rounded-lg border bg-muted/50 flex items-center">
                      <code className="flex-1 text-sm font-mono break-all">{keySecret}</code>
                    </div>
                    <Button variant="outline" size="icon" onClick={() => keySecret && navigator.clipboard.writeText(keySecret)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Button onClick={() => { setKeyDialogOpen(false); setKeySecret(null); setKeyPrefix(null); }}>Done</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Webhooks Section */}
      <Card className="p-6 md:block hidden">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3>Webhooks</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Receive real-time notifications about payment events
            </p>
          </div>
          <Button onClick={() => setWhDialogOpen(true)} disabled={creatingWebhook}>
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Add Webhook
          </Button>
        </div>

        <div className="border rounded-lg md:overflow-x-auto overflow-x-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Webhook URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Delivery</TableHead>
                <TableHead className="text-right w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(loadingWebhooks ? [] : webhooks).map((webhook) => (
                <TableRow key={webhook.id}>
                  <TableCell>
                    <div>
                      <p className="font-mono text-sm break-all">{webhook.url}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(webhook.eventTypes || []).map((event) => (
                        <Badge key={event} variant="secondary" className="text-xs">
                          {event}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={webhook.isActive ? "live" : "failed"} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {webhook.lastTriggeredAt ? new Date(webhook.lastTriggeredAt).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Webhooks Card List - mobile */}
      <Card className="md:hidden p-6">
        <div className="space-y-3">
          {(loadingWebhooks ? [] : webhooks).map((webhook) => (
            <div key={webhook.id} className="p-4 border rounded-lg grid gap-2">
              <div className="grid gap-1">
                <span className="text-xs text-muted-foreground">Webhook URL</span>
                <span className="font-mono text-sm break-all">{webhook.url}</span>
              </div>
              <div className="grid gap-1">
                <span className="text-xs text-muted-foreground">Events</span>
                <div className="flex flex-wrap gap-1">
                  {(webhook.eventTypes || []).map((event) => (
                    <Badge key={event} variant="secondary" className="text-xs">{event}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <StatusBadge status={webhook.isActive ? "live" : "failed"} />
                  <span className="text-sm text-muted-foreground">{webhook.lastTriggeredAt ? new Date(webhook.lastTriggeredAt).toLocaleString() : "-"}</span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleDeleteWebhook(webhook.id)}
                  className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={whDialogOpen} onOpenChange={setWhDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Webhook</DialogTitle>
            <DialogDescription>Validate URL before creating and store the secret securely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!whSecret ? (
              <>
                <div className="space-y-2">
                  <Label>Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={whUrl}
                      onChange={(e) => setWhUrl(e.target.value)}
                      placeholder="https://example.com/webhook"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await validateWebhookUrl({ url: whUrl, method: "POST", timeout: 10000 });
                          setWhValidation({ ok: res.valid && res.reachable, msg: `${res.message} (status ${res.statusCode}, ${res.responseTime}ms)` });
                        } catch {
                          setWhValidation({ ok: false, msg: "Validation request failed" });
                        }
                      }}
                    >
                      Validate
                    </Button>
                  </div>
                </div>
                {whValidation && (
                  <Alert className={whValidation.ok ? "border-success bg-success/5" : "border-destructive bg-destructive/5"}>
                    <AlertTitle>{whValidation.ok ? "URL is valid and reachable" : "Validation failed"}</AlertTitle>
                    <AlertDescription>{whValidation.msg}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label>Events</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(whEvents).map((evt) => (
                      <label key={evt} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={whEvents[evt]} onChange={(e) => setWhEvents({ ...whEvents, [evt]: e.target.checked })} />
                        {evt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={whDesc} onChange={(e) => setWhDesc(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={whActive} onCheckedChange={setWhActive} />
                  <span className="text-sm">Active</span>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    onClick={async () => {
                      setCreatingWebhook(true);
                      try {
                        const payload = { url: whUrl, eventTypes: Object.keys(whEvents).filter((k) => whEvents[k]), description: whDesc, isActive: whActive };
                        const created = await createWebhook(payload);
                        setWhSecret(created.secret || null);
                        await loadWebhooks();
                      } finally {
                        setCreatingWebhook(false);
                      }
                    }}
                    loading={creatingWebhook}
                    loadingText="Creating..."
                  >
                    Create Webhook
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Alert>
                  <AlertTitle>Save this secret now</AlertTitle>
                  <AlertDescription>The webhook secret will not be shown again. Store it securely.</AlertDescription>
                </Alert>
                <div>
                  <Label>Secret</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-10 px-3 rounded-lg border bg-muted/50 flex items-center">
                      <code className="flex-1 text-sm font-mono break-all">{whSecret}</code>
                    </div>
                    <Button variant="outline" size="icon" onClick={() => whSecret && navigator.clipboard.writeText(whSecret)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Button onClick={() => { setWhDialogOpen(false); setWhSecret(null); }}>Done</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      </div>
  );
}
